import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { AlertTriangle, Clock } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { minutesUntilRefreshExpiry } from '@/lib/authSession';

export default function SessionManager() {
  const {
    user,
    sessionStatus,
    extendSession,
    sessionExpiredOpen,
    setSessionExpiredOpen,
    logout,
  } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [extending, setExtending] = useState(false);
  const [minutesLeft, setMinutesLeft] = useState(null);

  useEffect(() => {
    if (sessionStatus !== 'warning' || !user) return undefined;
    const tick = () => setMinutesLeft(minutesUntilRefreshExpiry());
    tick();
    const id = setInterval(tick, 30000);
    return () => clearInterval(id);
  }, [sessionStatus, user]);

  const handleExtend = async () => {
    setExtending(true);
    try {
      await extendSession();
    } catch (_) {
      /* session-expired event maneja el modal */
    } finally {
      setExtending(false);
    }
  };

  const handleGoLogin = async () => {
    setSessionExpiredOpen(false);
    await logout();
    navigate('/login', { replace: true });
  };

  if (!user && !sessionExpiredOpen) return null;

  const onLoginPage = location.pathname === '/login';
  const showExpiredModal = sessionExpiredOpen && user && !onLoginPage;

  return (
    <>
      {user && sessionStatus === 'warning' && (
        <div
          className="fixed top-0 left-0 right-0 z-[100] bg-amber-50 border-b border-amber-200 px-4 py-2 flex flex-wrap items-center justify-center gap-3 text-xs text-amber-950 shadow-sm"
          data-testid="session-warning-banner"
        >
          <Clock className="w-4 h-4 shrink-0 text-amber-700" />
          <span>
            Tu sesión expira
            {minutesLeft != null ? ` en aproximadamente ${minutesLeft} min` : ' pronto'}
            . Guardá tu trabajo o renovala para seguir conectado.
          </span>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-7 text-xs border-amber-400 text-amber-900 hover:bg-amber-100"
            disabled={extending}
            onClick={handleExtend}
            data-testid="session-extend-button"
          >
            {extending ? 'Renovando…' : 'Mantener sesión'}
          </Button>
        </div>
      )}

      {showExpiredModal && (
        <div
          className="fixed inset-0 z-[110] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
          data-testid="session-expired-modal"
        >
          <div
            className="bg-white rounded-sm border border-brand-border shadow-xl max-w-md w-full p-6"
            role="dialog"
            aria-labelledby="session-expired-title"
            aria-modal="true"
          >
            <div className="flex items-start gap-3 mb-4">
              <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
              <div>
                <h2 id="session-expired-title" className="text-sm font-bold text-[#09090B]">
                  Sesión finalizada
                </h2>
                <p className="text-xs text-zinc-600 mt-2 leading-relaxed">
                  Tu sesión expiró por inactividad o por haber alcanzado el tiempo máximo permitido.
                  Iniciá sesión de nuevo para continuar. Los cambios no guardados pueden haberse perdido.
                </p>
              </div>
            </div>
            <div className="flex justify-end">
              <Button
                type="button"
                size="sm"
                className="bg-brand-primary text-white text-xs h-8"
                onClick={handleGoLogin}
                data-testid="session-expired-login-button"
              >
                Ir a iniciar sesión
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
